import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, ClipboardList, Upload, Settings } from 'lucide-react';

import { docsService } from '../../services/docs/docsService';

const DocsAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [rootProcessId, setRootProcessId] = useState('');
  const [docsEnabled, setDocsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const data = await docsService.getDocsConfig();
        setRootProcessId(data.config.rootProcessId || '');
        setDocsEnabled(data.config.docsEnabled);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load config');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await docsService.updateDocsConfig({
        rootProcessId: rootProcessId.trim() || null,
        docsEnabled,
      });
      setSaveMessage('Settings saved');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/docs')}
        className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to Docs
      </button>

      <h1 className="text-2xl font-bold text-gray-900 mb-6">Docs Settings</h1>

      {/* Quick links */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <Link
          to="/docs/admin/review"
          className="flex items-center p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
        >
          <ClipboardList className="w-5 h-5 text-blue-500 mr-3" />
          <div>
            <span className="font-medium text-gray-900">Review Queue</span>
            <p className="text-xs text-gray-500">
              Approve or reject pending items
            </p>
          </div>
        </Link>
        <Link
          to="/docs/admin/import"
          className="flex items-center p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
        >
          <Upload className="w-5 h-5 text-purple-500 mr-3" />
          <div>
            <span className="font-medium text-gray-900">Import</span>
            <p className="text-xs text-gray-500">Import documents from files</p>
          </div>
        </Link>
      </div>

      {/* Config */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <Settings className="w-5 h-5 text-gray-400 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">Configuration</h2>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-10 bg-gray-200 rounded" />
            <div className="h-10 bg-gray-200 rounded" />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Root Process ID
              </label>
              <input
                type="text"
                value={rootProcessId}
                onChange={(e) => setRootProcessId(e.target.value)}
                placeholder="UUID of the root process (e.g. Quote-to-Cash)"
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                The process displayed on the Docs home page. Leave empty for no
                root process.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Docs Enabled
                </label>
                <p className="text-xs text-gray-500">
                  Show the Docs module in the sidebar
                </p>
              </div>
              <button
                onClick={() => setDocsEnabled(!docsEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  docsEnabled ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    docsEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Save className="w-4 h-4 mr-1.5" />
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
              {saveMessage && (
                <span className="text-sm text-green-600">{saveMessage}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocsAdminPage;
